import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthStore } from '@root/app/store/auth.store';
import { AuthService } from '../../../core/services/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { AuthorizationContainerComponent } from '../authorization-container/authorization-container.component';
import { ActivatedRoute, Router } from '@angular/router';
import { FirstTimeResetPasswordModel } from '../../models';
import { APP_PATHS } from '@core/constants';
import { PasswordValidator } from '@core/helpers/password_validator';
import { MatchingPasswordsWithParams } from '@core/validators/password-matching.validator';
import { TextFieldComponent } from '@custom-components/general/form/text-field/text-field.component';
import { FilledButtonComponent } from '@custom-components/general/button/filled-button/filled-button.component';
import { AlertBannerComponent } from '@custom-components/feedback/alert-banner/alert-banner.component';

@Component({
  selector: 'xemex-set-new-password-page',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    AuthorizationContainerComponent,
    TextFieldComponent,
    FilledButtonComponent,
    AlertBannerComponent,
  ],
  providers: [AuthService],
  templateUrl: './set-new-password-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './set-new-password-page.component.scss',
})
export class SetNewPasswordPageComponent {
  readonly authStore = inject(AuthStore);

  isShowPasswordDifferenceError = false;

  setNewPasswordForm = new FormGroup(
    {
      password: new FormControl('', [Validators.required, PasswordValidator]),
      confirmPassword: new FormControl('', [Validators.required, PasswordValidator]),
    },
    {
      validators: MatchingPasswordsWithParams({
        passwordKey: 'password',
        confirmPasswordKey: 'confirmPassword',
      }),
    },
  );

  get setNewPassword() {
    return this.setNewPasswordForm.value;
  }

  get passwordControl() {
    return this.setNewPasswordForm.controls.password as FormControl;
  }

  get confirmPasswordControl() {
    return this.setNewPasswordForm.controls.confirmPassword as FormControl;
  }

  private readonly verificationToken: string;

  constructor() {
    const route = inject(ActivatedRoute);
    const router = inject(Router);

    this.authStore.clearAuthFeedback();
    this.verificationToken = route.snapshot.queryParamMap.get('t') ?? '';

    // Without the emailed token there is nothing to submit — the form would post
    // `token: null` and come back as an opaque 400. Send the user to the link-expired
    // page instead, which offers a way to request a fresh link.
    if (!this.verificationToken) {
      router.navigate(['/', APP_PATHS.Auth, APP_PATHS.LinkExpired]);
    }
  }

  onSetNewPassword() {
    if (this.setNewPasswordForm.invalid) {
      this.setNewPasswordForm.markAllAsTouched();
      return;
    }

    const resetPassword: FirstTimeResetPasswordModel = {
      new_password: this.setNewPassword.confirmPassword!,
      token: this.verificationToken,
    };
    this.authStore.resetPasswordMethod(resetPassword);
  }
}
